import { FavoriteIcon } from '@components/Icons/FavoriteIcon';
import { Likedicon } from '@components/Icons/LikedIcon';
import Loader from '@components/Loader';
import FeedPhotos from '@pages/Feeds/components/FeedPhotos';
import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { nyxId } from '../../../config';
import {
  FeedItem,
  FeedPagination,
  getBatchAlbumData,
  getContributorFeeds,
} from '../../../services/api';
import { formatPublishedTime } from '../../../utils/timeFormatter';
import FeedPostStyle, {
  FEED_ALBUM_THUMB_PORTRAIT,
} from '../../Feeds/components/FeedPostStyle';
import { ContributorFeedItem } from './ContributorFeedItem';

interface ContributorData {
  id: string;
  givenName: string;
  thumbnailUrl: string;
  videoDesktopUrl?: string;
  videoMobileUrl?: string;
  genre: string[];
  subGenre: string[];
  type: string;
  likes: number;
  gender: string;
  age: number;
  city: string;
  isLiked?: boolean;
  companionId?: string;
  albums: Array<{
    id: string;
    name: string;
    tracksCount: number;
    thumbnailUrl: string;
    duration: number;
    locales: string[];
    isLiked?: boolean;
    orgId?: string; // Organization ID for creations
    isCreation?: boolean; // Flag to identify creations
    category?: string;
  }>;
}

interface ContributorProfileFeedsProps {
  contributor: ContributorData;
  isLiked: boolean;
  onFavoriteToggle: () => Promise<void>;
  mobileHeader?: React.ReactNode;
}

export const ContributorProfileFeeds = memo(
  ({
    contributor,
    isLiked,
    onFavoriteToggle,
    mobileHeader,
  }: ContributorProfileFeedsProps) => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [albumDataMap, setAlbumDataMap] = useState<
      Map<string, { likes: number; publishedAt: number; isLiked: boolean }>
    >(new Map());
    const [isLoading, setIsLoading] = useState(true);
    const fetchedRef = useRef(false);

    // Feeds state
    const [feeds, setFeeds] = useState<FeedItem[]>([]);
    const [feedsPagination, setFeedsPagination] =
      useState<FeedPagination | null>(null);
    const [isFeedsLoading, setIsFeedsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const feedsFetchedRef = useRef(false);

    const FEEDS_LIMIT = 10;

    // Fetch contributor feeds
    const fetchFeeds = useCallback(
      async (offset: number = 0, append: boolean = false) => {
        if (offset === 0) {
          setIsFeedsLoading(true);
        } else {
          setIsLoadingMore(true);
        }

        try {
          const response = await getContributorFeeds(
            contributor.id,
            FEEDS_LIMIT,
            offset,
          );
          if (response) {
            setFeeds((prev) =>
              append ? [...prev, ...response.items] : response.items,
            );
            setFeedsPagination(response.pagination);
          }
        } catch (error) {
          console.error('Error fetching feeds:', error);
        } finally {
          setIsFeedsLoading(false);
          setIsLoadingMore(false);
        }
      },
      [contributor.id],
    );

    // Fetch feeds on component mount
    useEffect(() => {
      if (feedsFetchedRef.current) return;
      feedsFetchedRef.current = true;
      fetchFeeds(0, false);
    }, [fetchFeeds]);

    // Handle load more
    const handleLoadMore = useCallback(() => {
      if (!feedsPagination?.hasNext || isLoadingMore) return;
      const nextOffset = feedsPagination.offset + FEEDS_LIMIT;
      fetchFeeds(nextOffset, true);
    }, [feedsPagination, isLoadingMore, fetchFeeds]);

    // Fetch batch album data on component mount
    useEffect(() => {
      console.log(
        'Effect triggered - fetchedRef.current:',
        fetchedRef.current,
        'albums:',
        contributor.albums.length,
      );

      if (contributor.albums.length === 0 || fetchedRef.current) {
        console.log(
          'Skipping batch album data fetch - already fetched or no albums',
        );
        setIsLoading(false);
        return;
      }

      const fetchAlbumData = async () => {
        try {
          setIsLoading(true);
          const albumIds = contributor.albums.map((album) => album.id);
          const response = await getBatchAlbumData(albumIds);

          // Create a map for quick lookup
          const dataMap = new Map<
            string,
            { likes: number; publishedAt: number; isLiked: boolean }
          >();
          response.items.forEach((item) => {
            // Find the album to get isLiked status
            const album = contributor.albums.find((a) => a.id === item.id);
            dataMap.set(item.id, {
              likes: item.likes,
              publishedAt: item.publishedAt,
              isLiked: album?.isLiked ?? false,
            });
          });

          setAlbumDataMap(dataMap);
          fetchedRef.current = true;
        } finally {
          setIsLoading(false);
        }
      };

      fetchAlbumData();
    }, [contributor.id]);

    // Generate feed-style posts from albums, sorted by publishedAt descending
    const generateAlbumFeeds = () => {
      // Sort albums by publishedAt in descending order (newest first)
      const sortedAlbums = [...contributor.albums].sort((a, b) => {
        const aPublishedAt = albumDataMap.get(a.id)?.publishedAt ?? 0;
        const bPublishedAt = albumDataMap.get(b.id)?.publishedAt ?? 0;
        return bPublishedAt - aPublishedAt;
      });

      return sortedAlbums.map((album, index) => {
        const bgStyles = [
          'bg-[linear-gradient(180deg,rgba(97,244,255,0.2)_0%,rgba(97,244,255,0)_100%)]',
          'bg-[linear-gradient(180deg,rgba(255,222,120,0.2)_0%,rgba(255,222,120,0)_100%)]',
          'bg-[linear-gradient(180deg,rgba(255,120,180,0.2)_0%,rgba(255,120,180,0)_100%)]',
          'bg-[linear-gradient(180deg,rgba(120,255,120,0.2)_0%,rgba(120,255,120,0)_100%)]',
        ];

        // Get album data from map, with fallback values
        const albumData = albumDataMap.get(album.id);
        const likes = albumData?.likes ?? 0;
        const publishedAt =
          albumData?.publishedAt ?? Math.floor(Date.now() / 1000);
        const timeString = formatPublishedTime(publishedAt);
        const isLiked = albumData?.isLiked ?? album?.isLiked ?? false;

        return {
          name: contributor.givenName,
          username: `@${contributor.givenName.replace(/\s+/g, '')}`,
          time: timeString,
          category: 'album',
          description:
            album.tracksCount === 0
              ? t('contributor.newAlbum', { album: album.name, artist: contributor.givenName })
              : t('contributor.newAlbumWithTracks', { album: album.name, count: album.tracksCount, artist: contributor.givenName }),
          postImage: album.thumbnailUrl,
          likes: likes > 0 ? likes.toString() : '0',
          comments: '3.2K',
          saved: '1.6K',
          albumName: album.name,
          contributorImage: contributor.thumbnailUrl,
          albumId: album.id,
          widthHeight:
            album.category === 'book'
              ? FEED_ALBUM_THUMB_PORTRAIT
              : 'w-[200px] h-[200px]',
          bgStyle: bgStyles[index % bgStyles.length],
          isLiked,
          // Creation-specific fields
          orgId: album.orgId,
          isCreation: album.isCreation,
        };
      });
    };

    const albumFeeds = generateAlbumFeeds();

    // Handle likes update from FeedPostStyle
    const handleLikesUpdate = (
      albumId: string,
      newLikes: number,
      isLiked: boolean,
    ) => {
      // Update the album data map with new likes count and isLiked status
      setAlbumDataMap((prevMap) => {
        const newMap = new Map(prevMap);
        const albumData = newMap.get(albumId);
        if (albumData) {
          newMap.set(albumId, {
            ...albumData,
            likes: newLikes,
            isLiked,
          });
        }
        return newMap;
      });
    };

    return (
      <div id="contributor-feeds-content" className="animate-fade">
        {/* Mobile Header - Only visible on mobile */}
        {mobileHeader}

        <div className="w-full pb-[220px] pt-[10px] sm:m-auto sm:max-w-[500px]">
          {/* Sticky Profile Image - Floats above everything */}

          <div className="sticky top-4 z-30 flex justify-center pointer-events-none h-40">
            {!contributor.companionId && (
              <div className="rounded-full p-1 pointer-events-auto">
                <img
                  src={contributor.thumbnailUrl}
                  alt={contributor.givenName}
                  className="w-40 h-40 rounded-full border-2 border-white object-cover shadow-lg"
                />
              </div>
            )}
          </div>

          {/* Contributor Profile Card - First Item */}
          <div className="relative w-full min-h-[370px] rounded-2xl overflow-hidden backdrop-blur-sm border border-black/10 mb-6 -mt-42">
            {/* Cover Image - Full Box */}
            <div className="absolute inset-0">
              <img
                src={contributor.thumbnailUrl}
                alt={contributor.givenName}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/80 via-50% to-transparent" />
            </div>

            {/* Favorite Icon - Top Right - Hidden for now, need changes in api */}
            <div className="absolute top-4 right-4 z-10">
              <button
                onClick={onFavoriteToggle}
                className="flex items-center justify-center w-10 h-10 bg-black/40 backdrop-blur-sm rounded-full hover:bg-black/60 transition-colors duration-200">
                {isLiked ? (
                  <Likedicon width={20} height={23} />
                ) : (
                  <FavoriteIcon
                    fillOpacity={1}
                    fill="white"
                    width={20}
                    height={23}
                  />
                )}
              </button>
            </div>

            {/* Profile Content - Center Area */}
            <div className="absolute top-1/2 left-0 right-0 transform -translate-y-1/2 px-3">
              {/* Profile Image Spacer - maintains layout */}
              <div className="flex justify-center mb-2">
                <div className="relative w-40 h-40">
                  {/* Empty space to maintain layout */}
                </div>
              </div>

              {/* Name and Username */}
              <div className="text-center mb-2">
                <h1 className="text-white text-3xl font-bold mb-0.5 truncate">
                  {contributor.givenName}
                </h1>
                <p className="text-cyan-400 text-sm truncate">
                  @{contributor.givenName.replace(/\s+/g, '').toLowerCase()}
                </p>
              </div>

              {/* Bio/Description */}
              <div className="text-center mb-2">
                <span className="text-white/80 text-xs leading-tight capitalize bg-white/10 rounded-full px-2 py-0.5">
                  {contributor.genre.slice(0, 2).join(' & ')}
                </span>
              </div>

              {/* Album count - only show if > 0 */}
              {contributor.albums.length > 0 && (
                <div className="text-center mb-2 flex items-center gap-1 justify-center">
                  <span className="text-white font-bold text-sm">
                    {contributor.albums.length}
                  </span>
                  <span className="text-white/60 text-xs">{t('contributor.albums')}</span>
                </div>
              )}
            </div>

            {/* Stats - Always at Bottom */}
            {/* <div className="absolute bottom-0 left-0 right-0 px-3 pb-3">
              <div className="flex justify-between items-center px-2">
                <div className="flex items-center gap-1">
                  <span className="text-white font-bold text-sm">27</span>
                  <span className="text-white/60 text-xs">followers</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-white font-bold text-sm">32</span>
                  <span className="text-white/60 text-xs">following</span>
                </div>
                <div className="flex items-center gap-1">
                  <span className="text-white font-bold text-sm">
                    {contributor.albums.length}
                  </span>
                  <span className="text-white/60 text-xs">{t('contributor.albums')}</span>
                </div>
              </div>
            </div> */}
          </div>
          {/* Album Feeds Section */}
          <div>
            {contributor.id === nyxId && (
              <FeedPhotos indices={[1, 3, 6, 8, 10]} />
            )}
            {/* Loading State - Only for album feeds */}
            {isLoading && (
              <div className="flex justify-center items-center py-12">
                <Loader />
              </div>
            )}
            {/* Album Feeds */}
            {!isLoading &&
              albumFeeds.map((feedData, index) => (
                <FeedPostStyle
                  key={`album-${feedData.albumId}-${index}`}
                  widthHeight={feedData.widthHeight}
                  data={feedData}
                  bgStyle={feedData.bgStyle}
                  onLikesUpdate={handleLikesUpdate}
                  onPlayClick={
                    feedData.isCreation && feedData.orgId
                      ? () =>
                          navigate(
                            `/creations/track/${feedData.orgId}/${feedData.albumId}?ref=favorite`,
                          )
                      : undefined
                  }
                />
              ))}
            {contributor.id === nyxId && (
              <FeedPhotos indices={[12, 14, 16, 18, 20, 22, 24, 25]} />
            )}

            {/* Contributor Feeds Section */}
            {feeds.length > 0 && (
              <div className="mt-6">
                <h2 className="text-white text-lg font-semibold mb-4 px-1">
                  {t('contributor.posts')}
                </h2>
                {/* Feed Items */}
                {feeds.map((feed) => (
                  <ContributorFeedItem
                    key={feed.id}
                    feed={feed}
                    contributorName={contributor.givenName}
                    contributorThumbnail={contributor.thumbnailUrl}
                  />
                ))}
                {/* Load More Button */}
                {feedsPagination?.hasNext && (
                  <div className="flex justify-center mt-4 mb-8">
                    <button
                      onClick={handleLoadMore}
                      disabled={isLoadingMore}
                      className="px-6 py-2 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
                      {isLoadingMore ? (
                        <span className="flex items-center gap-2">
                          <Loader size="small" />
                          {t('common.loading')}
                        </span>
                      ) : (
                        t('contributor.loadMore')
                      )}
                    </button>
                  </div>
                )}
              </div>
            )}

            {/* Loading state for feeds */}
            {isFeedsLoading && (
              <div className="flex justify-center items-center py-8">
                <Loader />
              </div>
            )}

            {/* No Albums Message */}
            {!isLoading && albumFeeds.length === 0 && feeds.length === 0 && (
              <div className="mt-8 p-8 bg-white/5 rounded-lg text-center">
                <p className="text-white/60 text-lg">
                  {t('contributor.noContent')}
                </p>
                <p className="text-white/40 text-sm mt-2">
                  {t('contributor.checkBackLater', { name: contributor.givenName })}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  },
);

ContributorProfileFeeds.displayName = 'ContributorProfileFeeds';
